import React, { useCallback } from "react";
import { Nullable } from "sonobello.utilities.react";

import IMedicalConclusions from "../Types/IMedicalConclusions";
import IMedicalFormElementProps from "../Types/IMedicalFormElement";
import IMedicalQualification from "../Types/IMedicalQualification";

export interface ISideEffectMedicalQuestionDecoratorConfig {
  /** A mutator function called when a child form item changes which returns the new state of the medical
   * conclusions data.
   * @param previousConclusions - The previous state of the medical conclusions data.
   * @param newFormState - The new state of the medical form.
   * @returns The new state of the medical conclusions, or null if no change is needed.
   * @remarks This is useful for enforcing inter-form-item logic, such as clearing a conclusion value based on
   * the state of another form item.
   */
  transformMedicalConclusions: Nullable<
    (previousConclusions: IMedicalConclusions, formState: IMedicalQualification) => Nullable<IMedicalConclusions>
  >;

  /** A mutator function called when the form is updated which returns the new state of the medical form.
   * @param newFormState - The proposed new state of the medical form.
   * @returns The state of the medical form item to update, or null if no change is needed.
   * @remarks This is useful for enforcing inter-form-item logic, such as clearing a form value if another form item
   * disables it.
   */
  transformMedicalFormState: Nullable<(newFormState: IMedicalQualification) => Nullable<IMedicalQualification>>;

  /** The decorated form item component to be rendered. */
  ChildFormItem: React.FC<IMedicalFormElementProps>;
}

/**
 * A composable decorator for a child {@link IMedicalFormElementProps} component which allows for injected custom behaviors
 * or modification of medical conclusions or form state based on the provided configuration.
 */
const SideEffectMedicalQuestionDecorator: React.FC<
  IMedicalFormElementProps & ISideEffectMedicalQuestionDecoratorConfig
> = ({
  setMedicalConclusions,
  setMedicalFormState,
  transformMedicalConclusions,
  transformMedicalFormState,
  onChange,
  ChildFormItem,
  ...rest
}) => {
  const handleChange = useCallback(
    (newFormState: IMedicalQualification) => {
      if (transformMedicalConclusions)
        setMedicalConclusions(oldState => transformMedicalConclusions(oldState, newFormState) ?? oldState);
      if (transformMedicalFormState)
        setMedicalFormState(oldState => transformMedicalFormState(newFormState) ?? oldState);
      onChange(newFormState);
    },
    [onChange]
  );

  return (
    <ChildFormItem
      {...rest}
      onChange={handleChange}
      setMedicalConclusions={setMedicalConclusions}
      setMedicalFormState={setMedicalFormState}
    />
  );
};

export default SideEffectMedicalQuestionDecorator;
